import { Outlet, useRouter } from "@tanstack/react-router";
import clsx from "clsx";
import { type ReactNode, useCallback, useMemo, useState } from "react";
import { Toaster } from "../components/ui/Toast";
import { CommandPalette } from "../features/search/CommandPalette";
import { Column } from "./Column";
import { Rail } from "./Rail";
import { ShellContext } from "./shell-context";

const FOLDED_KEY = "kd.column.folded";

/** Whether the column was folded on the last visit. Without storage it starts open. */
function readFolded(): boolean {
  try {
    return window.localStorage.getItem(FOLDED_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * The shell (redesign spec §4.1): rail, column, page. Below 768 pixels the rail and the column are a drawer
 * that a page opens through `useShell().openMenu`. From 768 pixels the rail's sidebar button folds the column
 * away, to give a document or a diagram the width; the choice is kept in the browser and the drawer ignores it.
 * `children` replaces the route outlet in tests.
 */
export function AppShell({ children }: { children?: ReactNode }) {
  const router = useRouter();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const openSearch = useCallback(() => setSearchOpen(true), []);
  const shell = useMemo(() => ({ openMenu: () => setMenuOpen(true), openSearch }), [openSearch]);
  const closeMenu = () => setMenuOpen(false);
  const [folded, setFolded] = useState(readFolded);
  const toggleFolded = () => {
    const next = !folded;
    setFolded(next);
    try {
      window.localStorage.setItem(FOLDED_KEY, next ? "1" : "0");
    } catch {
      // No storage (a private window): the choice lives for this visit only.
    }
  };
  return (
    <ShellContext value={shell}>
      <div className="flex h-full">
        <div
          className={clsx(
            "fixed inset-y-0 left-0 z-40 md:static md:flex md:shadow-none",
            menuOpen ? "flex shadow-pop" : "hidden",
          )}
        >
          <Rail onNavigate={closeMenu} folded={folded} onToggleFolded={toggleFolded} />
          <aside
            id="kd-column"
            className={clsx(
              "h-full w-66 shrink-0 overflow-y-auto border-r border-line bg-column",
              folded && "md:hidden",
            )}
          >
            <Column onNavigate={closeMenu} />
          </aside>
        </div>
        {menuOpen ? (
          <button
            type="button"
            aria-label="Close menu"
            className="fixed inset-0 z-35 bg-ink/20 md:hidden"
            onClick={closeMenu}
          />
        ) : null}
        <main className="min-h-0 min-w-0 flex-1 overflow-auto">{children ?? <Outlet />}</main>
      </div>
      <CommandPalette open={searchOpen} onOpen={openSearch} onClose={() => setSearchOpen(false)} />
      <Toaster onNavigate={(to) => router.history.push(to)} />
    </ShellContext>
  );
}
